import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError } from '@/domains/api';

import type { IDefinitionSurvey } from '../../api/definitions-contracts';
import { internalCatalogueApi } from '../../api/internal-catalogue-api';
import type {
  IWriteInternalFieldOption,
  InternalFieldAccess
} from '../../api/internal-catalogue-contracts';
import type { InternalFieldType } from '../../api/internal-field-contracts';
import { KNOWN_INTERNAL_TYPES } from '../../internal-field-values';
import { refusalMessage } from '../../refusal';
import { ACCESS_OPTIONS, OptionRows, SELECT_NONE } from './choices';

/**
 * Authoring one staff field (SR-226) — `POST /admin/internal-fields`, then the first binding.
 *
 * **Two calls, deliberately, and the field is made first.** survey-api creates a field and binds
 * it to a survey through separate routes, and there is no body that does both. A field with no
 * binding is a legitimate state — it simply appears on no form — so the create is allowed to
 * succeed on its own and a binding that fails is said against a field that exists, rather than
 * rolled back into a refusal that loses what the author typed.
 *
 * **The code is derived from the name and stays editable.** survey-api's shape is
 * `^[a-z0-9]+([._][a-z0-9]+)*$` (§8.4) and the import's convention is `<survey>.<name>`, so the
 * suggestion prefixes the survey the author picked. It is a suggestion because a code is
 * permanent — nothing renames one — and the author may have a convention of their own.
 *
 * **Type and scope are chosen once.** Neither can be changed afterwards (`400 immutable-member`;
 * a scope change would have to rewrite every stored value through a composite key), and the
 * form says so where they are picked rather than after the fact.
 *
 * **Options are required for a choice type and refused for every other** (`400
 * invalid-options`), so the rows appear with the type and the form will not submit an empty set.
 * Nothing here validates a code or a label: survey-api's refusal is what the author reads, at
 * the target it names — **a 400 problem at `code` is said on the Code box** (a code under a list
 * prefix, `sys.` … `adv.`, SR-230, or `rep.` and `prior.`, SR-408), the draft kept; any other
 * refusal above the form.
 */
export function NewStaffField({
  surveys,
  onCreated,
  onCancel,
  onFailed
}: {
  surveys: IDefinitionSurvey[];
  onCreated: (code: string) => void;
  onCancel: () => void;
  onFailed: (message: string) => void;
}) {
  const [name, setName] = React.useState('');
  // `null` is "not typed", `''` is "typed empty" — one state instead of a value and a flag.
  const [code, setCode] = React.useState<Nullable<string>>(null);
  const [type, setType] = React.useState<InternalFieldType>('text');
  const [access, setAccess] = React.useState<InternalFieldAccess>('staff');
  const [surveyCode, setSurveyCode] = React.useState(SELECT_NONE);
  const [options, setOptions] = React.useState<IWriteInternalFieldOption[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [codeProblem, setCodeProblem] = React.useState<Nullable<string>>(null);
  const codeProblemId = React.useId();

  const choice = type === 'select' || type === 'multi_select';
  const effectiveCode = code ?? suggestCode(surveyCode, name);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setCodeProblem(null);
    try {
      const created = await internalCatalogueApi.create({
        code: effectiveCode.trim(),
        name: name.trim(),
        type,
        // Every field this screen makes hangs off the participant and the survey. The other two
        // scopes exist for values a cycle or one response owns, which nothing authors by hand
        // yet — offering them here would be offering a choice with no screen behind it.
        scope: 'participant_survey',
        access,
        options: choice ? options : undefined
      });
      if (surveyCode === SELECT_NONE) return onCreated(created.code);
      // The bind is a second call, and a refusal must not hide the field the first one made:
      // the form closes on the created code either way, so the list re-reads and shows it.
      // Left open, the only way back would be a retry — answered `409 duplicate-code`.
      try {
        await internalCatalogueApi.bind(created.code, surveyCode);
      } catch (error) {
        onFailed(
          refusalMessage(error, `The field was created; adding it to ${surveyCode} was refused`)
        );
      }
      onCreated(created.code);
    } catch (error) {
      const problems = error instanceof ApiError && error.status === 400 ? (error.problem?.problems ?? []) : [];
      const atCode = problems.filter((problem) => problem.target === 'code');
      if (atCode.length > 0) setCodeProblem(atCode.map((problem) => problem.reason).join(' '));
      if (atCode.length === 0 || atCode.length < problems.length) {
        onFailed(refusalMessage(error, 'Could not create the field'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New staff field</CardTitle>
        <CardDescription>
          It appears on the survey you bind it to, empty for every record, and on no
          advisor-facing screen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1 text-sm">
              Name
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Quality rating"
                aria-label="Name"
                required
              />
            </label>
            <label className="grid gap-1 text-sm">
              Code
              <Input
                value={effectiveCode}
                onChange={(event) => {
                  setCode(event.target.value);
                  setCodeProblem(null);
                }}
                placeholder="fa.quality_rating"
                className="font-mono"
                aria-label="Code"
                aria-invalid={codeProblem ? true : undefined}
                aria-describedby={codeProblem ? codeProblemId : undefined}
                required
              />
              {codeProblem && (
                <span id={codeProblemId} data-field-problem="code" className="text-xs text-destructive">
                  {codeProblem}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                Permanent — nothing renames a code once a value holds it.
              </span>
            </label>
            <label className="grid gap-1 text-sm">
              Type
              <Select
                value={type}
                onValueChange={(value) => setType(value as InternalFieldType)}
              >
                <SelectTrigger aria-label="Type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((shape) => (
                    <SelectItem key={shape} value={shape}>
                      {shape}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                Chosen once: the type cannot be changed afterwards.
              </span>
            </label>
            <label className="grid gap-1 text-sm">
              Who may see it
              <Select
                value={access}
                onValueChange={(value) => setAccess(value as InternalFieldAccess)}
              >
                <SelectTrigger aria-label="Who may see it">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCESS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-1 text-sm sm:col-span-2">
              Where it appears
              <Select value={surveyCode} onValueChange={setSurveyCode}>
                <SelectTrigger aria-label="Where it appears">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SELECT_NONE}>No survey yet</SelectItem>
                  {surveys.map((survey) => (
                    <SelectItem key={survey.code} value={survey.code}>
                      {survey.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                One survey to start with; the rest are bound from the field itself, and the
                section it sits in is set under Forms.
              </span>
            </label>
          </div>

          {choice && (
            <fieldset className="grid gap-2">
              <legend className="text-sm">Values</legend>
              <OptionRows rows={options} retirable={false} onChange={setOptions} />
              {options.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  A choice field needs at least one value — one with none is refused.
                </p>
              )}
            </fieldset>
          )}

          <div className="flex gap-2">
            <Button type="submit" disabled={busy || (choice && options.length === 0)}>
              Create
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** What an author may choose — the types this app reads (`internal-field-values.ts`' own list,
 *  the sole reader of a type) less `rich_text`: nothing authors one by hand, and the record page
 *  draws it through the kit's editor. Derived rather than transcribed, so an eleventh type
 *  arrives in one place. */
const FIELD_TYPES: InternalFieldType[] = KNOWN_INTERNAL_TYPES.filter(
  (type) => type !== 'rich_text'
);

/** `Quality rating` on FA → `fa.quality_rating` — the import's own convention, so a field
 *  authored here sorts and reads beside the 738 that came from Salesforce. */
function suggestCode(surveyCode: string, name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (slug === '') return '';
  if (surveyCode === SELECT_NONE) return slug;
  const prefix = surveyCode.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return `${prefix}.${slug}`;
}

